import type { IconName } from '../components/Icon'

export type ModuleMetric = {
  label: string
  value: string
  hint: string
}

export type ModuleSection = {
  label: string
  items: string[]
}

export type ModuleDefinition = {
  id: string
  title: string
  subtitle: string
  icon: IconName
  action: string
  metrics: ModuleMetric[]
  sections: ModuleSection[]
  columns: string[]
  rows: string[][]
}

const standardRows = {
  transport: [
    ['Route A — North', '8:00 AM', '12 clients', 'Van 04', 'Ready'],
    ['Route B — Central', '8:15 AM', '9 clients', 'Van 02', 'In progress'],
    ['Route C — South', '8:30 AM', '7 clients', 'Van 07', 'Ready'],
    ['Afternoon return', '2:30 PM', '28 clients', '3 vehicles', 'Scheduled'],
  ],
  timecard: [
    ['Maria S.', 'Care staff', '7:58 AM', '—', 'Clocked in'],
    ['David K.', 'Driver', '8:04 AM', '—', 'Clocked in'],
    ['Linda P.', 'Nurse', '7:45 AM', '4:12 PM', 'Complete'],
    ['James R.', 'Kitchen', '8:10 AM', '—', 'Clocked in'],
  ],
  clinical: [
    ['Client 1001', 'Blood pressure', '128 / 78', 'Today, 9:15 AM', 'Normal'],
    ['Client 1002', 'Blood sugar', '142 mg/dL', 'Today, 9:22 AM', 'Review'],
    ['Client 1003', 'Pulse', '74 bpm', 'Today, 9:30 AM', 'Normal'],
    ['Client 1004', 'Weight', '156 lb', 'Yesterday', 'Normal'],
  ],
  activities: [
    ['Morning exercise', '9:00 AM', 'Main room', '18', 'Planned'],
    ['Music & memory', '10:30 AM', 'Activity room', '12', 'Planned'],
    ['Lunch social', '12:00 PM', 'Dining room', '32', 'Planned'],
    ['Arts workshop', '1:30 PM', 'Studio', '10', 'Planned'],
  ],
  food: [
    ['Breakfast', 'Oatmeal, fruit, milk', '8:30 AM', '34', 'Published'],
    ['Morning snack', 'Yogurt, water', '10:30 AM', '28', 'Published'],
    ['Lunch', 'Chicken, rice, vegetables', '12:00 PM', '38', 'Published'],
    ['Afternoon snack', 'Crackers, juice', '2:30 PM', '31', 'Draft'],
  ],
  billing: [
    ['INV-24071', 'Sample client', 'Medicaid', '$1,240.00', 'Ready'],
    ['INV-24072', 'Sample client', 'Private pay', '$385.00', 'Pending'],
    ['CLM-18921', 'Batch 07/29', 'Medicare', '$4,820.00', 'Submitted'],
    ['CLM-18922', 'Batch 07/29', 'Medicaid', '$3,160.00', 'Review'],
  ],
  tasks: [
    ['Review care plan', 'Clinical', 'Today', 'High', 'Open'],
    ['Confirm transportation', 'Transport', 'Today', 'Normal', 'Open'],
    ['Eligibility follow-up', 'Billing', 'Tomorrow', 'High', 'In progress'],
    ['Update meal enrollment', 'Food', 'Friday', 'Normal', 'Open'],
  ],
  crm: [
    ['Evergreen Community', 'Facility', 'Follow-up', 'Today', 'Warm'],
    ['Sample referral', 'Referral', 'Assessment', 'Tomorrow', 'New'],
    ['Community partner', 'Partner', 'Call back', 'Friday', 'Active'],
    ['Family inquiry', 'Lead', 'Tour scheduled', 'Next week', 'Warm'],
  ],
  rpm: [
    ['Client 1001', 'Blood pressure', '128 / 78', '9:15 AM', 'Normal'],
    ['Client 1002', 'Glucose', '168 mg/dL', '9:22 AM', 'Alert'],
    ['Client 1003', 'Oxygen', '97%', '9:30 AM', 'Normal'],
    ['Client 1004', 'Weight', '156 lb', 'Yesterday', 'Normal'],
  ],
  organize: [
    ['Care plan review', 'Weekly task', 'Clinical team', 'Wednesday', 'Open'],
    ['Transportation check', 'Appointment', 'Dispatch', 'Thursday', 'Planned'],
    ['Assessment due', 'Compliance', 'Nursing', 'Friday', 'Due'],
    ['Family follow-up', 'Note', 'Care manager', 'Friday', 'Open'],
  ],
}

export const MODULE_CATALOG: Record<string, ModuleDefinition> = {
  transport: {
    id: 'transport',
    title: 'Transport',
    subtitle: 'Routes, vehicles, pickups and EVV transportation logs',
    icon: 'transport',
    action: 'Create route',
    metrics: [
      { label: 'Today’s routes', value: '4', hint: '3 active, 1 scheduled' },
      { label: 'Clients riding', value: '28', hint: 'Across all shifts' },
      { label: 'Vehicles ready', value: '6', hint: '1 needs review' },
      { label: 'Open exceptions', value: '2', hint: 'Driver follow-up' },
    ],
    sections: [
      { label: 'Operations', items: ['Transportation Log', 'Route Schedule', 'Client Schedule', 'Dispatch Board'] },
      { label: 'Fleet', items: ['Ambulance', 'Drivers', 'Vehicles', 'Routes', 'Field Trip Locations', 'Transportation Range'] },
      { label: 'Reports', items: ['Driver Report', 'Transportation Monthly', 'Transportation By Payer', 'EVV Mileage Report'] },
    ],
    columns: ['Route', 'Pickup', 'Manifest', 'Vehicle', 'Status'],
    rows: standardRows.transport,
  },
  timecard: {
    id: 'timecard',
    title: 'Time Card',
    subtitle: 'Employee check-in, check-out and attendance history',
    icon: 'clock',
    action: 'Clock in',
    metrics: [
      { label: 'Clocked in', value: '24', hint: 'Current shift' },
      { label: 'Completed', value: '11', hint: 'Today' },
      { label: 'Late arrivals', value: '2', hint: 'Needs review' },
      { label: 'Missing punches', value: '1', hint: 'Open exception' },
    ],
    sections: [
      { label: 'Time card', items: ['Check-in / Check-out', 'Punching Log Details', 'Attendance History', 'Signature Confirmation'] },
      { label: 'Employee', items: ['Employee Schedule', 'Shift Assignment', 'Documents', 'Exceptions'] },
    ],
    columns: ['Employee', 'Role', 'Check in', 'Check out', 'Status'],
    rows: standardRows.timecard,
  },
  clinical: {
    id: 'clinical',
    title: 'Clinical',
    subtitle: 'Daily vitals, nursing schedule and clinical follow-up',
    icon: 'clinical',
    action: 'Add vitals',
    metrics: [
      { label: 'Vitals today', value: '38', hint: '32 complete' },
      { label: 'Rechecks', value: '4', hint: 'Due today' },
      { label: 'Alerts', value: '2', hint: 'Requires review' },
      { label: 'Assessments due', value: '7', hint: 'Next 7 days' },
    ],
    sections: [
      { label: 'Nursing', items: ['Vitals', 'Recheck Vitals', 'Today’s Vitals Details', 'Vitals Profile', 'Nursing Schedule'] },
      { label: 'Clinical records', items: ['Assessments', 'MARS', 'Care Plans', 'Hospitalization', 'Incidents', 'Orders'] },
      { label: 'Monitoring', items: ['Blood Pressure / Pulse', 'Blood Sugar', 'Weight', 'Fall Risk', 'Wandering Risk'] },
    ],
    columns: ['Client', 'Reading', 'Value', 'Recorded', 'Status'],
    rows: standardRows.clinical,
  },
  activities: {
    id: 'activities',
    title: 'Activities',
    subtitle: 'Program calendar, participation and activity logs',
    icon: 'activities',
    action: 'Add activity',
    metrics: [
      { label: 'Programs today', value: '8', hint: 'Across 3 rooms' },
      { label: 'Participants', value: '42', hint: 'Unique clients' },
      { label: 'Open seats', value: '18', hint: 'Today’s sessions' },
      { label: 'Logs pending', value: '3', hint: 'Needs completion' },
    ],
    sections: [
      { label: 'Programs', items: ['Play Activity', 'Program Calendar', 'Daily Schedule', 'Participation'] },
      { label: 'Logs', items: ['Activity Log', 'Monthly Activity Log', 'Care Plan Activity', 'Attendance'] },
    ],
    columns: ['Activity', 'Time', 'Location', 'Participants', 'Status'],
    rows: standardRows.activities,
  },
  food: {
    id: 'food',
    title: 'Food',
    subtitle: 'Meal schedule, CACFP enrollment and diet plans',
    icon: 'food',
    action: 'Plan meal',
    metrics: [
      { label: 'Meals today', value: '4', hint: 'Published schedule' },
      { label: 'Enrolled clients', value: '36', hint: 'CACFP program' },
      { label: 'Special diets', value: '9', hint: 'Active plans' },
      { label: 'Missing counts', value: '2', hint: 'Needs review' },
    ],
    sections: [
      { label: 'Meal service', items: ['Food Schedule', 'Meal Auto Schedule', 'Meal Count', 'Meal Tracking By Client', 'Meal Tracking By Facility'] },
      { label: 'CACFP', items: ['Eligibility Attendance', 'Client Meal Enrollment', 'CACFP Master List', 'Meal Count Type'] },
      { label: 'Clinical', items: ['Diet Plans', 'Allergies', 'Special Diets', 'Nutrition Notes'] },
    ],
    columns: ['Meal', 'Menu', 'Service time', 'Count', 'Status'],
    rows: standardRows.food,
  },
  reports: {
    id: 'reports',
    title: 'Reports',
    subtitle: 'Operational, clinical, compliance and financial reporting',
    icon: 'reports',
    action: 'Run report',
    metrics: [
      { label: 'Report groups', value: '9', hint: 'All departments' },
      { label: 'Saved reports', value: '14', hint: 'My reports' },
      { label: 'Scheduled', value: '6', hint: 'Automatic delivery' },
      { label: 'Due items', value: '7', hint: 'Compliance' },
    ],
    sections: [
      { label: 'Admission', items: ['Admission', 'Admission Clinical Profile Missing', 'Admission Missing Notes', 'Discharge', 'Client Diagnosis'] },
      { label: 'Administrator', items: ['Anniversary', 'Badge', 'Birthday', 'Emergency Contact', 'Physician Reports', 'Client Status'] },
      { label: 'Billing', items: ['Active Eligibility', 'Census Data', 'Eligibility Expiration', 'Pending Authorization', 'Claim Not Generated'] },
      { label: 'Clinical', items: ['Allergy', 'Assessment', 'Behavior', 'Care Plan Compliance', 'Hospitalization', 'Incident', 'Vitals Alert'] },
      { label: 'Compliance', items: ['Assessments Due', 'Assessment Log', 'Employee Certification Expiration', 'Facility Expiration', 'Audit Report'] },
      { label: 'Meal', items: ['CACFP Eligibility', 'Diet Plan', 'Meal Count', 'Meal Tracking'] },
      { label: 'Attendance', items: ['Attendance By Payer', 'Attendance Exception', 'Daily Clocking', 'Facility Summary'] },
      { label: 'Transportation', items: ['Driver', 'Transportation Monthly', 'Transportation By Payer', 'EVV Mileage'] },
      { label: 'SDOH', items: ['Assessment Due', 'SDOH Missing'] },
    ],
    columns: ['Report', 'Category', 'Last run', 'Format', 'Status'],
    rows: [
      ['Client Status', 'Administrator', 'Today', 'PDF', 'Ready'],
      ['Assessments Due', 'Compliance', 'Yesterday', 'Excel', 'Ready'],
      ['Census Data', 'Billing', 'Jul 28', 'Excel', 'Ready'],
      ['Vitals Alert', 'Clinical', 'Jul 28', 'PDF', 'Ready'],
    ],
  },
  admin: {
    id: 'admin',
    title: 'Admin',
    subtitle: 'Facility setup, users, templates and system configuration',
    icon: 'admin',
    action: 'Add user',
    metrics: [
      { label: 'Active users', value: '18', hint: '2 administrators' },
      { label: 'Roles', value: '7', hint: 'Permission sets' },
      { label: 'Forms', value: '26', hint: 'System and custom' },
      { label: 'Expirations', value: '3', hint: 'Next 30 days' },
    ],
    sections: [
      { label: 'Access', items: ['Users', 'Roles', 'Permissions', 'Login Log'] },
      { label: 'Facility', items: ['DayCareCenter Profile', 'Schedule', 'Program Calendar', 'Report Setting', 'Client Tab Setting'] },
      { label: 'Directories', items: ['Physician', 'Pharmacy', 'Restaurant', 'Vendor', 'Document Type', 'Gifts'] },
      { label: 'Forms', items: ['System Forms', 'Main Menu Forms', 'Custom Forms', 'Templates'] },
      { label: 'Care plans', items: ['Regular Care Plan Template', 'Checklist Care Plan Template', 'Care Plan Activity'] },
      { label: 'Transportation', items: ['Ambulance', 'Driver', 'Vehicles', 'Routes', 'Field Trip Locations'] },
      { label: 'Workflow', items: ['Workflow Task', 'Workflow Plan', 'Email Template', 'Workflow Status'] },
      { label: 'SDOH', items: ['Social Determinant', 'Category', 'Risk Level', 'Advance ADL'] },
    ],
    columns: ['User', 'Role', 'Last active', 'Facility', 'Status'],
    rows: [
      ['Administrator', 'Admin', 'Now', 'Sunrise', 'Active'],
      ['Clinical manager', 'Clinical', 'Today', 'Sunrise', 'Active'],
      ['Dispatcher', 'Transport', 'Today', 'Sunrise', 'Active'],
      ['Billing specialist', 'Billing', 'Yesterday', 'Sunrise', 'Active'],
    ],
  },
  billing: {
    id: 'billing',
    title: 'Billing',
    subtitle: 'Payers, claims, invoices and payment management',
    icon: 'billing',
    action: 'Create claim',
    metrics: [
      { label: 'Open claims', value: '24', hint: '$28,420 total' },
      { label: 'Ready to submit', value: '8', hint: '2 batches' },
      { label: 'Pending invoices', value: '6', hint: '$3,280 total' },
      { label: 'Exceptions', value: '3', hint: 'Needs review' },
    ],
    sections: [
      { label: 'Setup', items: ['Billing Clients', 'Payer', 'HCPCS Codes', 'Private Pay Items'] },
      { label: 'Claims', items: ['Claim Batches', 'Claim Batches EVV', 'Eligibility', 'Authorizations', 'Claim Status'] },
      { label: 'Receivables', items: ['Invoices', 'Payments', 'Statements', 'A/R Aging', 'QuickBooks Export'] },
    ],
    columns: ['Reference', 'Client / batch', 'Payer', 'Amount', 'Status'],
    rows: standardRows.billing,
  },
  crm: {
    id: 'crm',
    title: 'CRM',
    subtitle: 'Leads, relationships, outreach and referral pipeline',
    icon: 'crm',
    action: 'Add lead',
    metrics: [
      { label: 'New leads', value: '12', hint: 'This month' },
      { label: 'Follow-ups', value: '7', hint: 'Due this week' },
      { label: 'Tours', value: '4', hint: 'Scheduled' },
      { label: 'Conversions', value: '3', hint: 'This month' },
    ],
    sections: [
      { label: 'Pipeline', items: ['Leads', 'Opportunities', 'Tours', 'Follow-ups', 'Conversions'] },
      { label: 'Relationships', items: ['Contacts', 'Organizations', 'Sources', 'Tags'] },
      { label: 'Outreach', items: ['Campaigns', 'Email Templates', 'Activities', 'Notes'] },
    ],
    columns: ['Name', 'Type', 'Next step', 'Due', 'Stage'],
    rows: standardRows.crm,
  },
  referrals: {
    id: 'referrals',
    title: 'Referrals',
    subtitle: 'Referral intake, assessment and admission tracking',
    icon: 'referral',
    action: 'New referral',
    metrics: [
      { label: 'New', value: '6', hint: 'Awaiting contact' },
      { label: 'Assessments', value: '4', hint: 'Scheduled' },
      { label: 'Pending', value: '3', hint: 'Eligibility review' },
      { label: 'Admitted', value: '5', hint: 'This month' },
    ],
    sections: [
      { label: 'Intake', items: ['New Referrals', 'Contact Attempts', 'Documents', 'Eligibility'] },
      { label: 'Admission', items: ['Assessments', 'Tours', 'Pending Admission', 'Admitted'] },
      { label: 'Sources', items: ['Referral Sources', 'Partners', 'Campaigns', 'Performance'] },
    ],
    columns: ['Referral', 'Source', 'Next step', 'Due', 'Stage'],
    rows: standardRows.crm,
  },
  tasks: {
    id: 'tasks',
    title: 'Tasks',
    subtitle: 'Open tasks, past-due work and appointments',
    icon: 'tasks',
    action: 'Create task',
    metrics: [
      { label: 'Open tasks', value: '18', hint: 'Across all teams' },
      { label: 'Past due', value: '3', hint: 'Needs attention' },
      { label: 'Appointments due', value: '5', hint: 'Today' },
      { label: 'Today’s tasks', value: '9', hint: '6 remaining' },
    ],
    sections: [
      { label: 'Work', items: ['Open Tasks', 'Past Due Tasks', 'Today’s Appointments', 'Today’s Tasks'] },
      { label: 'Manage', items: ['My Tasks', 'Team Tasks', 'Recurring Tasks', 'Completed'] },
    ],
    columns: ['Task', 'Department', 'Due', 'Priority', 'Status'],
    rows: standardRows.tasks,
  },
  rpm: {
    id: 'rpm',
    title: 'RPM',
    subtitle: 'Remote patient monitoring, alerts and device readings',
    icon: 'activities',
    action: 'Record reading',
    metrics: [
      { label: 'Monitored clients', value: '16', hint: 'Active program' },
      { label: 'Readings today', value: '42', hint: '4 device types' },
      { label: 'Alerts', value: '2', hint: 'Requires review' },
      { label: 'Missing readings', value: '3', hint: 'Follow-up due' },
    ],
    sections: [
      { label: 'Monitoring', items: ['Client Dashboard', 'Readings', 'Alerts', 'Trends'] },
      { label: 'Program', items: ['Devices', 'Care Team', 'Schedules', 'Thresholds', 'Notifications'] },
      { label: 'Reports', items: ['Compliance', 'Clinical Summary', 'Monthly Activity', 'Billing Minutes'] },
    ],
    columns: ['Client', 'Reading', 'Value', 'Recorded', 'Status'],
    rows: standardRows.rpm,
  },
  organize: {
    id: 'organize',
    title: 'Organize',
    subtitle: 'Weekly appointments, tasks, activities and due items',
    icon: 'calendar',
    action: 'Add item',
    metrics: [
      { label: 'Appointments', value: '12', hint: 'This week' },
      { label: 'Weekly tasks', value: '18', hint: '7 complete' },
      { label: 'Assessments due', value: '7', hint: 'Next 7 days' },
      { label: 'Notes', value: '5', hint: 'Needs follow-up' },
    ],
    sections: [
      { label: 'Planner', items: ['Weekly Appointment', 'Weekly Appointment Activity', 'Weekly Task', 'Weekly Task Activity'] },
      { label: 'Follow-up', items: ['Assessment Due', 'Notes', 'Client List', 'Reminders'] },
    ],
    columns: ['Item', 'Type', 'Owner', 'Due', 'Status'],
    rows: standardRows.organize,
  },
}

